/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import { useState, useEffect } from "react";
import { convertFromHTML, EditorState, ContentState } from "draft-js";
import { convertToHTML } from "draft-convert";
import { removeHTMLTag } from "config/Utilities";

export default function useSkillDescription({
  description,
  onEdit,
  isHtml,
  maxLength = null,
  setContentLength,
}: {
  description: string;
  onEdit: (newDescription: string) => void;
  isHtml?: boolean;
  maxLength?: null | number;
  setContentLength?: (length: number) => void;
}) {
  const [editorState, setEditorState] = useState(EditorState.createEmpty());

  useEffect(() => {
    if (description) {
      let contentState = ContentState.createFromText(description);
      if (isHtml) {
        const blocksFromHTML = convertFromHTML(description);
        contentState = ContentState.createFromBlockArray(
          blocksFromHTML.contentBlocks
        );
      }
      setEditorState(EditorState.createWithContent(contentState));
    }
  }, []);

  const _handleChange = (newEditorState: EditorState) => {
    const newContent = newEditorState.getCurrentContent();
    if (editorState.getCurrentContent() !== newContent) {
      if (isHtml) {
        onEdit(convertToHTML(newContent));
      } else {
        onEdit(newContent.getPlainText());
      }
    }
    setEditorState(newEditorState);
    if (setContentLength) {
      setContentLength(removeHTMLTag(convertToHTML(newContent)).length);
    }
  };

  const _getLengthOfSelectedText = () => {
    const currentSelection = editorState.getSelection();
    const isCollapsed = currentSelection.isCollapsed();

    let length = 0;

    if (!isCollapsed) {
      const currentContent = editorState.getCurrentContent();
      const startKey = currentSelection.getStartKey();
      const endKey = currentSelection.getEndKey();
      if (startKey === endKey) {
        length +=
          currentSelection.getEndOffset() - currentSelection.getStartOffset();
      } else {
        const startBlock = currentContent.getBlockForKey(startKey);
        const startBlockTextLength = startBlock.getLength();
        const startSelectedTextLength =
          startBlockTextLength - currentSelection.getStartOffset();
        const endSelectedTextLength = currentSelection.getEndOffset();
        const keyAfterEnd = currentContent.getKeyAfter(endKey);

        let currentKey = startKey;

        while (currentKey && currentKey !== keyAfterEnd) {
          if (currentKey === startKey) {
            length += startSelectedTextLength + 1;
          } else if (currentKey === endKey) {
            length += endSelectedTextLength;
          } else {
            length += currentContent.getBlockForKey(currentKey).getLength() + 1;
          }

          currentKey = currentContent.getKeyAfter(currentKey);
        }
      }
    }

    return length;
  };

  const _handleBeforeInput = () => {
    if (!maxLength) {
      return "not-handled";
    }
    const currentContent = editorState.getCurrentContent();
    const currentContentLength = removeHTMLTag(
      convertToHTML(currentContent)
    ).length;
    const selectedTextLength = _getLengthOfSelectedText();
    if (currentContentLength - selectedTextLength > maxLength - 1) {
      return "handled";
    }
    return "not-handled";
  };

  const _handlePastedText = (pastedText) => {
    if (!maxLength) {
      return "not-handled";
    }
    const currentContent = editorState.getCurrentContent();
    const currentContentLength = removeHTMLTag(
      convertToHTML(currentContent)
    ).length;

    const selectedTextLength = _getLengthOfSelectedText();
    if (
      currentContentLength + pastedText.length - selectedTextLength >
      maxLength - 1
    ) {
      return "handled";
    }

    return "not-handled";
  };

  return { editorState, _handleChange, _handleBeforeInput, _handlePastedText };
}
